import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Admin from "@/pages/Admin";
import RydeHome from "@/pages/RydeHome";
import RydeWorld from "@/pages/RydeWorld";
import RydeTrack from "@/pages/RydeTrack";
import { RydePrivacyPage, RydeRefundsPage, RydeTermsPage } from "@/pages/RydeLegal";
import { RydeWhatsAppFab } from "@/components/ryde/Ryde";
import { trackEvent, trackPage } from "@/lib/tracking";

const GlobalChrome = () => {
  const { pathname } = useLocation();
  if (pathname === "/admin") return null;
  return <RydeWhatsAppFab />;
};

const RouteTracker = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === "/admin") return undefined;

    trackPage(pathname);

    const reachedDepths = new Set();
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 0 ? Math.round((window.scrollY / max) * 100) : 100;
      [25, 50, 75, 90].forEach((depth) => {
        if (pct >= depth && !reachedDepths.has(depth)) {
          reachedDepths.add(depth);
          trackEvent("scroll_depth", `${depth}%`, pathname);
        }
      });
    };

    const seenSections = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const name = entry.target.getAttribute("data-track-section");
        if (!name || seenSections.has(name)) return;
        seenSections.add(name);
        trackEvent("section_view", name, pathname);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.35 });

    const sectionTimer = setTimeout(() => {
      document.querySelectorAll("[data-track-section]").forEach((el) => observer.observe(el));
    }, 150);

    const onClick = (e) => {
      const explicit = e.target.closest("[data-track-event]");
      if (explicit) {
        trackEvent(
          explicit.getAttribute("data-track-event"),
          explicit.getAttribute("data-track-label") || explicit.textContent,
          pathname,
        );
        return;
      }

      const checkoutLink = e.target.closest('a[href="/checkout"]');
      if (checkoutLink) {
        trackEvent(
          "cta_click",
          checkoutLink.getAttribute("data-testid") || checkoutLink.textContent,
          pathname,
        );
        return;
      }

      const paymentLink = e.target.closest('a[href*="razorpay.me"]');
      if (paymentLink) trackEvent("payment_link_click", paymentLink.textContent, pathname);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("click", onClick, true);
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onClick, true);
      clearTimeout(sectionTimer);
      observer.disconnect();
    };
  }, [pathname]);

  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App font-body grain">
      <BrowserRouter>
        <GlobalChrome />
        <RouteTracker />
        <Routes>
          <Route path="/" element={<RydeHome />} />
          <Route path="/world" element={<RydeWorld />} />
          <Route path="/track" element={<RydeTrack />} />
          <Route path="/privacy" element={<RydePrivacyPage />} />
          <Route path="/refunds" element={<RydeRefundsPage />} />
          <Route path="/terms" element={<RydeTermsPage />} />
          <Route path="/legal" element={<Navigate to="/terms" replace />} />
          <Route path="/product" element={<Navigate to="/#kit" replace />} />
          <Route path="/checkout" element={<Navigate to="/#order" replace />} />
          <Route path="/pay" element={<Navigate to="/#order" replace />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" theme="dark" />
    </div>
  );
}

export default App;
